import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { INSURANCE_TYPES } from '../../../../constants';
import { onboardingQuestionnaireActions } from '../../../../store';
import { DOCUMENT_TYPES, ONBOARDING_TAB_IDS } from '../constants';
import { GENERAL_INFO_FORM_SCHEMA } from '../components/forms/general/validationSchema';
import { getGeneralInformationInitialValues } from '../components/forms/general/hooks/utils';
import { INSURANCE_DOCUMENT_SUB_TYPES } from '../components/forms/insurance/constants';
import { INSURANCE_FORM_SCHEMA } from '../components/forms/insurance/validationSchema';
import {
  findInsuranceByType,
  getInsuranceCardDocument,
  getInsuranceInitialValues,
  hasInsuranceNameAndMemberId,
} from '../components/forms/insurance/hooks/utils';
import { AGREEMENT_FORM_SCHEMA } from '../components/forms/agreement/validationSchema';
import { getAgreementInitialValues } from '../components/forms/agreement/hooks/utils';
import { REFERRAL_FORM_SCHEMA } from '../components/forms/referral/validationSchema';
import { getReferralInitialValues } from '../components/forms/referral/hooks/utils';
import { initializeTabStatuses } from '../../common/hooks';

import type { AppDispatch, RootState } from '../../../../store';
import type { QuestionnaireTabStatusEntry } from '../../common/hooks';
import type { IOnboardingQuestionnaireInsurance } from '../components/forms/insurance/types';
import type { IOnboardingQuestionnaireDocument, OnboardingTabId } from '../types';

export default function useInitializeOnboardingTabStatuses() {
  const dispatch = useDispatch<AppDispatch>();
  const generalInfo = useSelector(
    (state: RootState) =>
      state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.general_info,
  );
  const insurances = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.insurances,
  );
  const referralInfo = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.referral,
  );
  const agreement = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.agreement,
  );
  const providerFullName = useSelector(
    (state: RootState) =>
      state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.general_info
        ?.provider_full_name ?? '',
  );
  const noSecondaryInsurance = useSelector(
    (state: RootState) =>
      state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.no_secondary_insurance ?? false,
  );
  const documents = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.documents,
  );

  useEffect(() => {
    const cardSides = getInsuranceCardSideFlags(documents);
    if (noSecondaryInsurance) {
      dispatch(
        onboardingQuestionnaireActions.updateOnboardingTabStatus({
          tabId: ONBOARDING_TAB_IDS.SECONDARY_INSURANCE,
          status: { error: false, success: true },
        }),
      );
    }

    const tabEntries: QuestionnaireTabStatusEntry<OnboardingTabId>[] = [
      {
        tabId: ONBOARDING_TAB_IDS.GENERAL,
        values: getGeneralInformationInitialValues(generalInfo),
        schema: GENERAL_INFO_FORM_SCHEMA,
      },
      getOnboardingInsuranceTabEntry(
        ONBOARDING_TAB_IDS.PRIMARY_INSURANCE,
        findInsuranceByType(insurances, INSURANCE_TYPES.PRIMARY),
        cardSides.hasPrimaryFront,
        cardSides.hasPrimaryBack,
      ),
      ...(noSecondaryInsurance
        ? []
        : [
            getOnboardingInsuranceTabEntry(
              ONBOARDING_TAB_IDS.SECONDARY_INSURANCE,
              findInsuranceByType(insurances, INSURANCE_TYPES.SECONDARY),
              cardSides.hasSecondaryFront,
              cardSides.hasSecondaryBack,
            ),
          ]),
      {
        tabId: ONBOARDING_TAB_IDS.REFERRAL,
        values: getReferralInitialValues(referralInfo),
        schema: REFERRAL_FORM_SCHEMA,
        hasNonFormikSavedValues:
          documents?.some(
            (document) => document.document_type === DOCUMENT_TYPES.REFERRAL_REPORT,
          ) ?? false,
      },
      {
        tabId: ONBOARDING_TAB_IDS.AGREEMENT,
        values: getAgreementInitialValues(agreement, providerFullName),
        schema: AGREEMENT_FORM_SCHEMA,
      },
    ];

    initializeTabStatuses(tabEntries, {
      updateTabStatus: onboardingQuestionnaireActions.updateOnboardingTabStatus,
      dispatch,
    });
  }, [
    agreement,
    dispatch,
    documents,
    generalInfo,
    insurances,
    noSecondaryInsurance,
    providerFullName,
    referralInfo,
  ]);
}

function getInsuranceCardSideFlags(
  documents: IOnboardingQuestionnaireDocument[] | null | undefined,
) {
  return {
    hasPrimaryFront: Boolean(
      getInsuranceCardDocument(documents, INSURANCE_DOCUMENT_SUB_TYPES.PRIMARY_FRONT),
    ),
    hasPrimaryBack: Boolean(
      getInsuranceCardDocument(documents, INSURANCE_DOCUMENT_SUB_TYPES.PRIMARY_BACK),
    ),
    hasSecondaryFront: Boolean(
      getInsuranceCardDocument(documents, INSURANCE_DOCUMENT_SUB_TYPES.SECONDARY_FRONT),
    ),
    hasSecondaryBack: Boolean(
      getInsuranceCardDocument(documents, INSURANCE_DOCUMENT_SUB_TYPES.SECONDARY_BACK),
    ),
  };
}

function getOnboardingInsuranceTabEntry(
  tabId: OnboardingTabId,
  insurance: IOnboardingQuestionnaireInsurance | undefined,
  hasFront: boolean,
  hasBack: boolean,
): QuestionnaireTabStatusEntry<OnboardingTabId> {
  const values = getInsuranceInitialValues(insurance);
  return {
    tabId,
    values,
    schema: INSURANCE_FORM_SCHEMA,
    extraRequirementMet: hasInsuranceNameAndMemberId(values) || (hasFront && hasBack),
    hasNonFormikSavedValues: hasFront || hasBack,
  };
}
